/**
 * Chunk library for the seeded daily generator (spec 6.4). Each chunk is
 * 8–32 rows of hand-authored, hand-validated pattern in the level row format
 * (no 'o'/'C'/'F' — the stitcher inserts those), plus metadata:
 *
 *  - entry: lanes that are ride-in-able on the FIRST row (floor-level kinds)
 *  - exit:  lanes the player can occupy grounded on the LAST row
 *  - difficulty 1–5, tags for flavor
 *
 * Solvability by construction: every chunk is beatable from any entry lane
 * and can leave via any exit lane; the stitcher only joins chunks with
 * exit(A) ∩ entry(B) ≠ ∅ and glues 2 floor rows at every seam (which also
 * guarantees no cross-seam wide gaps or gap→tunnel traps).
 */
import { rep, every, F, GAP } from "../levels/build";
import type { GravityPreset } from "../config/tuning";

const ALL_G: GravityPreset[] = ["floaty", "normal", "heavy"];
const SOFT_G: GravityPreset[] = ["floaty", "normal"]; // jumps too long for heavy

export interface Chunk {
  id: string;
  difficulty: 1 | 2 | 3 | 4 | 5;
  tags: string[];
  entry: number[];
  exit: number[];
  rows: string[];
  /** Gravity presets this chunk is solvable under (R2 §4.2). */
  gravities: GravityPreset[];
  /** Minimum throttle its hazards demand (runway rule applies above 1.0). */
  minThrottle: number;
}

const ALL = [0, 1, 2, 3, 4, 5, 6];
const MID = [2, 3, 4];

export const CHUNKS: Chunk[] = [
  // --- difficulty 1: calm ---------------------------------------------------
  { id: "straight-8", difficulty: 1, tags: ["breather"], entry: ALL, exit: ALL,
    rows: rep(F, 8), gravities: ALL_G, minThrottle: 1 },
  { id: "straight-12", difficulty: 1, tags: ["breather"], entry: ALL, exit: ALL,
    rows: rep(F, 12), gravities: ALL_G, minThrottle: 1 },
  { id: "edge-posts", difficulty: 1, tags: ["blocks", "breather"], entry: ALL, exit: ALL,
    rows: [F, "1_____1", ...rep(F, 5), "1_____1", ...rep(F, 4)] , gravities: ALL_G, minThrottle: 1 },
  { id: "lone-block", difficulty: 1, tags: ["blocks"], entry: ALL, exit: ALL,
    rows: [...rep(F, 3), "___1___", ...rep(F, 4)] , gravities: ALL_G, minThrottle: 1 },
  { id: "boost-taste", difficulty: 1, tags: ["pads", "breather"], entry: ALL, exit: ALL,
    rows: [...rep(F, 2), "___B___", ...rep(F, 9)] , gravities: ALL_G, minThrottle: 1 },

  // --- difficulty 2 ---------------------------------------------------------
  { id: "single-weave", difficulty: 2, tags: ["blocks"], entry: ALL, exit: ALL,
    rows: [F, ...every(4, "__1____", "____1__", "_1___1_"), ...rep(F, 2)] , gravities: ALL_G, minThrottle: 1 },
  { id: "one-gap", difficulty: 2, tags: ["gaps"], entry: ALL, exit: ALL,
    rows: [...rep(F, 3), GAP, ...rep(F, 4)] , gravities: SOFT_G, minThrottle: 1 },
  { id: "two-gaps", difficulty: 2, tags: ["gaps"], entry: ALL, exit: ALL,
    rows: [...rep(F, 2), GAP, ...rep(F, 3), GAP, ...rep(F, 4)] , gravities: SOFT_G, minThrottle: 1 },
  { id: "hazard-shoulders", difficulty: 2, tags: ["hazards"], entry: ALL, exit: ALL,
    rows: [F, "XX___XX", ...rep(F, 3), "XX___XX", ...rep(F, 3)] , gravities: SOFT_G, minThrottle: 1 },
  { id: "tunnel-short", difficulty: 2, tags: ["tunnel"], entry: ALL, exit: MID,
    rows: [...rep(F, 2), ...rep("..TTT..", 4), "..___..", "..___.."] , gravities: ALL_G, minThrottle: 1 },
  { id: "double-posts", difficulty: 2, tags: ["blocks"], entry: ALL, exit: ALL,
    rows: [F, ...every(4, "_11____", "____11_"), ...rep(F, 4)] , gravities: ALL_G, minThrottle: 1 },
  { id: "boost-runway", difficulty: 2, tags: ["pads"], entry: ALL, exit: ALL,
    rows: [F, "___B___", ...rep(F, 11)] , gravities: ALL_G, minThrottle: 1 },

  // --- difficulty 3 ---------------------------------------------------------
  { id: "gap-then-block", difficulty: 3, tags: ["gaps", "blocks"], entry: ALL, exit: ALL,
    rows: [...rep(F, 2), GAP, ...rep(F, 3), "__1_1__", ...rep(F, 3), GAP, ...rep(F, 3)] , gravities: SOFT_G, minThrottle: 1 },
  { id: "wall-openings", difficulty: 3, tags: ["blocks"], entry: ALL, exit: ALL,
    rows: [F, ...every(4, "11___11", "111_111", "_11_11_"), ...rep(F, 2)] , gravities: ALL_G, minThrottle: 1 },
  { id: "hazard-slalom", difficulty: 3, tags: ["hazards"], entry: ALL, exit: ALL,
    rows: [F, ...every(4, "X__X__X", "_X_X_X_", "XX___XX"), ...rep(F, 2)] , gravities: SOFT_G, minThrottle: 1 },
  { id: "tunnel-run", difficulty: 3, tags: ["tunnel"], entry: ALL, exit: MID,
    rows: [...rep(F, 2), ...rep("..TTT..", 6), "..___..", "..___.."] , gravities: ALL_G, minThrottle: 1 },
  { id: "narrow-road", difficulty: 3, tags: ["narrow"], entry: MID, exit: MID,
    rows: [...rep("..___..", 8), "..___..", "..___.."] , gravities: ALL_G, minThrottle: 1 },
  { id: "sticky-field", difficulty: 3, tags: ["pads"], entry: ALL, exit: ALL,
    rows: [F, "__S_S__", ...rep(F, 5), "____1__", ...rep(F, 4)] , gravities: ALL_G, minThrottle: 1 },
  { id: "height-steps", difficulty: 3, tags: ["blocks"], entry: ALL, exit: ALL,
    rows: [F, ...every(4, "__2____", "____2__", "_2___2_"), ...rep(F, 2)] , gravities: ALL_G, minThrottle: 1 },
  { id: "gap-boost-gap", difficulty: 3, tags: ["gaps", "pads"], entry: ALL, exit: ALL,
    rows: [F, "___B___", ...rep(F, 2), GAP, ...rep(F, 3), GAP, ...rep(F, 4)] , gravities: SOFT_G, minThrottle: 1 },

  // --- difficulty 4 ---------------------------------------------------------
  { id: "hazard-wall-jump", difficulty: 4, tags: ["hazards"], entry: ALL, exit: ALL,
    rows: [...rep(F, 2), "XXXXXXX", ...rep(F, 3), "XXXXXXX", ...rep(F, 4)] , gravities: SOFT_G, minThrottle: 1 },
  { id: "wall-maze", difficulty: 4, tags: ["blocks"], entry: ALL, exit: ALL,
    rows: [F, ...every(4, "111_111", "11_1_11", "1_111_1", "_111_1_"), ...rep(F, 2)] , gravities: ALL_G, minThrottle: 1 },
  { id: "narrow-single", difficulty: 4, tags: ["narrow"], entry: MID, exit: [3],
    rows: [...rep("..___..", 4), ...rep("..._...", 4), "..._...", "..._..."] , gravities: ALL_G, minThrottle: 1 },
  { id: "tunnel-long", difficulty: 4, tags: ["tunnel"], entry: ALL, exit: MID,
    rows: [...rep(F, 2), ...rep(".TTTTT.", 5), ...rep("..TTT..", 4), "..___.."] , gravities: ALL_G, minThrottle: 1 },
  { id: "hazard-weave-dense", difficulty: 4, tags: ["hazards"], entry: ALL, exit: ALL,
    rows: [F, ...every(4, "XXX_XXX", "XX_X_XX", "X_XXX_X"), ...rep(F, 2)] , gravities: SOFT_G, minThrottle: 1 },
  { id: "gaps-offset", difficulty: 4, tags: ["gaps", "blocks"], entry: ALL, exit: ALL,
    rows: [F, GAP, ...rep(F, 3), "_1___1_", ...rep(F, 2), GAP, ...rep(F, 3), "__1_1__", ...rep(F, 3)] , gravities: SOFT_G, minThrottle: 1 },
  { id: "sticky-trap", difficulty: 4, tags: ["pads", "blocks"], entry: ALL, exit: ALL,
    rows: [F, "_S___S_", ...rep(F, 6), "11___11", ...rep(F, 3), "_11_11_", ...rep(F, 3)] , gravities: ALL_G, minThrottle: 1 },
  { id: "monolith-lanes", difficulty: 4, tags: ["blocks"], entry: ALL, exit: ALL,
    rows: [F, ...every(4, "__3_3__", "_3___3_"), ...rep(F, 2)] , gravities: ALL_G, minThrottle: 1 },

  // --- difficulty 5: expert -------------------------------------------------
  { id: "double-gap", difficulty: 5, tags: ["gaps"], entry: ALL, exit: ALL,
    rows: [...rep(F, 3), GAP, GAP, ...rep(F, 6)] , gravities: SOFT_G, minThrottle: 1 },
  { id: "boosted-double-gap", difficulty: 5, tags: ["gaps", "pads"], entry: ALL, exit: ALL,
    rows: ["___B___", ...rep(F, 4), GAP, GAP, ...rep(F, 5)] , gravities: SOFT_G, minThrottle: 1 },
  { id: "monolith-gauntlet", difficulty: 5, tags: ["blocks"], entry: ALL, exit: ALL,
    rows: [F, ...every(4, "_3_3_3_", "3__3__3", "__3_3__"), ...rep(F, 2)] , gravities: ALL_G, minThrottle: 1 },
  { id: "single-lane-long", difficulty: 5, tags: ["narrow"], entry: [3], exit: [3],
    rows: [...rep("..._...", 8), "..._...", "..._..."] , gravities: ALL_G, minThrottle: 1 },
  { id: "hazard-gap-mix", difficulty: 5, tags: ["hazards", "gaps"], entry: ALL, exit: ALL,
    rows: [F, "XXXXXXX", ...rep(F, 3), GAP, ...rep(F, 3), "XX_X_XX", ...rep(F, 3)] , gravities: SOFT_G, minThrottle: 1 },
  { id: "everything-burst", difficulty: 5, tags: ["blocks", "hazards", "gaps"], entry: ALL, exit: ALL,
    rows: [F, "11_1_11", ...rep(F, 3), GAP, ...rep(F, 3), "X_XXX_X", ...rep(F, 3), "__2_2__", ...rep(F, 3)] , gravities: SOFT_G, minThrottle: 1 },

  // --- R2 new parts (throttle gaps, floaty arcs, heavy drums, U, ramps, ice) —
  // every chunk tagged "r2" for the daily's new-part-presence CI assertion
  { id: "throttle-gap-3", difficulty: 4, tags: ["r2", "gaps", "throttle"], entry: ALL, exit: ALL,
    rows: [...rep(F, 8), GAP, GAP, GAP, ...rep(F, 6)], gravities: ["normal"], minThrottle: 1.3 },
  { id: "throttle-gap-brake", difficulty: 5, tags: ["r2", "gaps", "throttle", "blocks"], entry: ALL, exit: ALL,
    rows: [...rep(F, 8), GAP, GAP, GAP, ...rep(F, 4), "_11_11_", ...rep(F, 3), "__1_1__", ...rep(F, 3)], gravities: ["normal"], minThrottle: 1.3 },
  { id: "throttle-runway-boost", difficulty: 4, tags: ["r2", "gaps", "throttle", "pads"], entry: ALL, exit: ALL,
    rows: ["___B___", ...rep(F, 7), GAP, GAP, GAP, ...rep(F, 5)], gravities: ["normal"], minThrottle: 1.3 },
  { id: "floaty-arc", difficulty: 3, tags: ["r2", "gaps", "floaty"], entry: ALL, exit: ALL,
    rows: [...rep(F, 4), GAP, GAP, GAP, GAP, ...rep(F, 6)], gravities: ["floaty"], minThrottle: 1 },
  { id: "floaty-arc-chain", difficulty: 4, tags: ["r2", "gaps", "floaty"], entry: ALL, exit: ALL,
    rows: [...rep(F, 3), GAP, GAP, GAP, GAP, ...rep(F, 4), GAP, GAP, GAP, GAP, ...rep(F, 5)], gravities: ["floaty"], minThrottle: 1 },
  { id: "heavy-hopscotch", difficulty: 3, tags: ["r2", "gaps", "heavy"], entry: ALL, exit: ALL,
    rows: [...rep(F, 2), GAP, F, F, GAP, F, F, GAP, F, F, GAP, ...rep(F, 3)], gravities: ["heavy"], minThrottle: 1 },
  { id: "heavy-drums", difficulty: 4, tags: ["r2", "gaps", "heavy"], entry: ALL, exit: ALL,
    rows: [...rep(F, 2), GAP, F, F, GAP, F, F, GAP, F, F, GAP, F, F, GAP, ...rep(F, 4)], gravities: ["heavy"], minThrottle: 1 },
  { id: "tunnel-hole-run", difficulty: 4, tags: ["r2", "tunnel", "holes"], entry: ALL, exit: ALL,
    rows: [...rep(F, 2), ...rep("TTTTTTT", 3), "UUUUUUU", ...rep("TTTTTTT", 3), "UUUUUUU", ...rep("TTTTTTT", 2)], gravities: SOFT_G, minThrottle: 1 },
  { id: "ramp-ridge", difficulty: 3, tags: ["r2", "ramps"], entry: ALL, exit: ALL,
    rows: [...rep(F, 2), "///////", "1111111", "1111111", "\\\\\\\\\\\\\\", ...rep(F, 4)], gravities: ALL_G, minThrottle: 1 },
  { id: "ramp-lip-launch", difficulty: 3, tags: ["r2", "ramps"], entry: ALL, exit: ALL,
    rows: [...rep(F, 2), "///////", ...rep(F, 6)], gravities: ALL_G, minThrottle: 1 },
  { id: "ice-lane", difficulty: 3, tags: ["r2", "ice"], entry: ALL, exit: ALL,
    rows: [...rep(F, 2), ...rep("IIIIIII", 6), ...rep(F, 4)], gravities: ALL_G, minThrottle: 1 },
  { id: "ice-slalom", difficulty: 4, tags: ["r2", "ice", "blocks"], entry: ALL, exit: ALL,
    rows: [...rep(F, 2), "IIIIIII", "IIIIIII", "I1III1I", "IIIIIII", "III1III", "IIIIIII", "IIIIIII", ...rep(F, 3)], gravities: ALL_G, minThrottle: 1 },
  { id: "ice-tunnel-approach", difficulty: 5, tags: ["r2", "ice", "tunnel", "holes"], entry: ALL, exit: ALL,
    rows: [...rep(F, 2), ...rep("IIIIIII", 3), ...rep("TTTTTTT", 3), "UUUUUUU", ...rep("TTTTTTT", 2), ...rep(F, 3)], gravities: SOFT_G, minThrottle: 1 },

  // --- SkyRoads update: narrow ribbons + lateral island hops ----------------
  { id: "narrow-funnel", difficulty: 3, tags: ["r2", "narrow"], entry: ALL, exit: ALL,
    rows: [F, "._____.", "..___..", "..___..", "..._...", "..._...", "..._...", "..___..", "._____.", F], gravities: ALL_G, minThrottle: 1 },
  { id: "ribbon-shift", difficulty: 3, tags: ["r2", "narrow"], entry: ALL, exit: ALL,
    rows: [F, "_____..", "___....", "___....", "______.", "______.", "....___", "....___", "....___", F], gravities: ALL_G, minThrottle: 1 },
  { id: "island-zigzag", difficulty: 4, tags: ["r2", "narrow", "gaps"], entry: MID, exit: MID,
    rows: ["..___..", "..__...", ".._....", ".._....", ".._....", "...._..", "...._..", "...._..", ".._....", ".._....", "..___..", "..___.."], gravities: SOFT_G, minThrottle: 1 },
  { id: "narrow-hazard-bridge", difficulty: 4, tags: ["r2", "narrow", "hazards"], entry: MID, exit: MID,
    rows: ["..___..", "..._...", "..._...", "...X...", "..._...", "..._...", "...X...", "..._...", "..._...", "..___.."], gravities: SOFT_G, minThrottle: 1 },
];
